import React from 'react';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { connect } from 'react-redux';
import { Field, reduxForm } from 'redux-form';
import Button from 'react-bootstrap/lib/Button';
import { injectIntl } from 'react-intl';
import { flowRight as compose } from 'lodash';
import withStyles from 'isomorphic-style-loader/withStyles';
import CommonFormComponent from '../../../components/CommonField/CommonFormComponent';
import PaymentGatewayLogoUploader from '../../../components/siteadmin/ConfigSettingsForm/PaymentGatewayLogoUploader';
import Loader from '../../../components/Loader/Loader';
import { withQuery } from '../../../hoc/withGraphql';
import { decode } from '../../../helpers/queryEncryption';
import showToaster from '../../../helpers/toasterMessages/showToaster';
import { demoRestriction } from '../../../helpers/DemoRestriction/demoRestriction';
import { updatePaymentGatewayConfiguration } from '../../../actions/siteadmin/updatePaymentGatewayConfiguration';
import messages from '../../../locale/jsonMessages';
import paypalLogo from '/public/PaymentGateway/paypal.svg';
import stripeLogo from '/public/PaymentGateway/stripe.svg';
import pawapayLogo from '/public/PaymentGateway/pawapay.svg';
import cp from '../../../components/commonStyle.css';
import s from './PaymentGatewayConfig.css';

const query = gql`
  query getPaymentGatewaySettings($gateway: String!) {
    getPaymentGatewaySettings(gateway: $gateway) {
      name
      encryptValue
    }
    getPaymentGatewayDisplaySettings(gateway: $gateway) {
      name
      paymentName
      paymentSubtitle
    }
  }
`;

class PaymentGatewayConfig extends React.Component {
    static propTypes = { gateway: PropTypes.oneOf(['paypal', 'stripe', 'pawapay']).isRequired };

    componentDidMount() {
        if (!this.props.data.loading) this.initialize();
    }

    componentDidUpdate(previousProps) {
        if (previousProps.data.loading && !this.props.data.loading) this.initialize();
    }

    initialize = () => {
        const values = {};
        this.props.data.getPaymentGatewaySettings?.forEach(item => { values[item.name] = decode(item.encryptValue); });
        const displaySettings = this.props.data.getPaymentGatewayDisplaySettings;
        values.paymentName = displaySettings?.paymentName || displaySettings?.name || '';
        values.paymentSubtitle = displaySettings?.paymentSubtitle || '';
        if (this.props.gateway === 'paypal') {
            values.paypalEnvironment = values.paypalEnvironment || (String(values.paypalHost || '').includes('sandbox') ? 'sandbox' : 'production');
            values.paypalSandboxClientId = values.paypalSandboxClientId || (values.paypalEnvironment === 'sandbox' ? values.paypalClientId : '');
            values.paypalSandboxSecret = values.paypalSandboxSecret || (values.paypalEnvironment === 'sandbox' ? values.paypalSecret : '');
            values.paypalProductionClientId = values.paypalProductionClientId || (values.paypalEnvironment === 'production' ? values.paypalClientId : '');
            values.paypalProductionSecret = values.paypalProductionSecret || (values.paypalEnvironment === 'production' ? values.paypalSecret : '');
        } else if (this.props.gateway === 'stripe') {
            values.stripeEnvironment = values.stripeEnvironment || (String(values.stripePublishableKey || '').startsWith('pk_test_') ? 'test' : 'live');
            values.stripeTestPublishableKey = values.stripeTestPublishableKey || (values.stripeEnvironment === 'test' ? values.stripePublishableKey : '');
            values.stripeTestSecretKey = values.stripeTestSecretKey || (values.stripeEnvironment === 'test' ? values.stripeSecretKey : '');
            values.stripeLivePublishableKey = values.stripeLivePublishableKey || (values.stripeEnvironment === 'live' ? values.stripePublishableKey : '');
            values.stripeLiveSecretKey = values.stripeLiveSecretKey || (values.stripeEnvironment === 'live' ? values.stripeSecretKey : '');
        } else {
            values.pawapayEnvironment = values.pawapayEnvironment || 'sandbox';
        }
        this.props.initialize(values);
    };

    submit = async values => {
        if (await this.props.demoRestriction({})) return;
        const result = await this.props.updatePaymentGatewayConfiguration({ gateway: this.props.gateway, ...values });
        if (result?.status === '200') {
            showToaster({ messageId: 'updateConfigSettings', toasterType: 'success' });
        } else {
            showToaster({ messageId: 'commonError', toasterType: 'error', requestMessage: result?.errorMessage });
        }
    };

    renderField(name, message, type = 'text', required = true) {
        const { formatMessage } = this.props.intl;
        const text = typeof message === 'string' ? message : formatMessage(message);
        return <Field name={name} type={type} component={CommonFormComponent}
            placeholder={text} labelText={text}
            inputClass='commonInputField' isRequiredInput={required} />;
    }

    render() {
        const { gateway, data, handleSubmit, submitting, intl: { formatMessage } } = this.props;
        if (data.loading) return <Loader type="text" />;
        const isPayPal = gateway === 'paypal';
        const isStripe = gateway === 'stripe';
        const displayName = isPayPal ? 'PayPal' : isStripe ? 'Stripe' : 'PawaPay';
        const defaultLogo = isPayPal ? paypalLogo : isStripe ? stripeLogo : pawapayLogo;
        return <div>
            <h1 className='siteAdminHeading'>{isPayPal ? formatMessage(messages.paypalSettings) : isStripe ? formatMessage(messages.stripeSettings) : 'Paramètres du compte PawaPay'}</h1>
            <PaymentGatewayLogoUploader
                title={`${displayName} Gateway Logo`}
                name={`${gateway}GatewayLogo`}
                defaultLogo={defaultLogo}
            />
            <form onSubmit={handleSubmit(this.submit)}>
                <div className={s.gatewayConfigRows}>
                    <section className={s.environmentSection}>
                        <h3>Informations affichées aux utilisateurs</h3>
                        <div className={s.environmentFields}>
                            {this.renderField('paymentName', 'Libellé du moyen de paiement')}
                            {this.renderField('paymentSubtitle', 'Sous-titre (facultatif)', 'text', false)}
                        </div>
                    </section>
                    <div className={s.environmentRow}>
                    <Field name={isPayPal ? 'paypalEnvironment' : isStripe ? 'stripeEnvironment' : 'pawapayEnvironment'} component={CommonFormComponent}
                        labelText={formatMessage(messages.gatewayEnvironment)} inputClass='commonSelectField'>
                        <option value={isPayPal || !isStripe ? 'sandbox' : 'test'}>{formatMessage(messages.sandboxLabel)}</option>
                        <option value={isPayPal ? 'production' : 'live'}>{formatMessage(messages.productionLabel)}</option>
                    </Field>
                    </div>
                    <section className={s.environmentSection}>
                        <h3>{formatMessage(messages.productionLabel)}</h3>
                        <div className={s.environmentFields}>
                            {isPayPal ? this.renderField('paypalProductionClientId', messages.paypalProductionClientId) : isStripe ? this.renderField('stripeLivePublishableKey', messages.stripeLivePublishableKey) : this.renderField('pawapayLiveToken', 'Jeton API Production PawaPay', 'password')}
                            {isPayPal ? this.renderField('paypalProductionSecret', messages.paypalProductionSecret, 'password') : isStripe ? this.renderField('stripeLiveSecretKey', messages.stripeLiveSecretKey, 'password') : null}
                        </div>
                    </section>
                    <section className={s.environmentSection}>
                        <h3>{formatMessage(messages.sandboxLabel)}</h3>
                        <div className={s.environmentFields}>
                            {isPayPal ? this.renderField('paypalSandboxClientId', messages.paypalSandboxClientId) : isStripe ? this.renderField('stripeTestPublishableKey', messages.stripeTestPublishableKey) : this.renderField('pawapaySandboxToken', 'Jeton API Sandbox PawaPay', 'password')}
                            {isPayPal ? this.renderField('paypalSandboxSecret', messages.paypalSandboxSecret, 'password') : isStripe ? this.renderField('stripeTestSecretKey', messages.stripeTestSecretKey, 'password') : null}
                        </div>
                    </section>
                </div>
                <div className='singleBtnFlexEnd'>
                    <Button className={cp.btnPrimary} type="submit" disabled={submitting}>{formatMessage(messages.save)}</Button>
                </div>
            </form>
        </div>;
    }
}

const Form = reduxForm({ form: 'PaymentGatewayConfig' })(PaymentGatewayConfig);

export default compose(
    injectIntl,
    withStyles(cp, s),
    connect(null, { demoRestriction, updatePaymentGatewayConfiguration }),
    withQuery(query, { options: props => ({ variables: { gateway: props.gateway }, fetchPolicy: 'network-only', ssr: false }) })
)(Form);
